import { useState, useEffect, useCallback, useRef } from 'react';
import { galleryItems, GalleryItem } from '@/mocks/gallery';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const categories = ['All', 'Portraits', 'Performances'] as const;
type CategoryFilter = typeof categories[number];

function GalleryCard({ item, index, onClick, extraClass = '' }: { item: GalleryItem; index: number; onClick: () => void; extraClass?: string }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.04, rootMargin: '0px 0px -20px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const getSpanClass = () => {
    if (item.aspectRatio === 'portrait') return 'row-span-2';
    if (item.aspectRatio === 'landscape') return 'row-span-1';
    return 'row-span-1';
  };

  return (
    <div
      ref={cardRef}
      className={`group relative overflow-hidden cursor-pointer w-full h-full ${getSpanClass()} ${extraClass} transition-all duration-700 ease-out`}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
        transition: `opacity 0.7s cubic-bezier(0.25, 0.1, 0.25, 1) ${index * 0.06}s, transform 0.7s cubic-bezier(0.25, 0.1, 0.25, 1) ${index * 0.06}s`,
      }}
      onClick={onClick}
    >
      <img
        src={item.src}
        alt={item.alt}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        loading="lazy"
      />
      {/* Overlay on hover */}
      <div className="absolute inset-0 bg-primary-950/0 group-hover:bg-primary-950/40 transition-all duration-500 flex items-end">
        <div className="p-4 w-full translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
          {item.caption && (
            <p className="text-foreground-50 text-xs tracking-[0.08em]">{item.caption}</p>
          )}
          <p className="text-foreground-300 text-[10px] tracking-[0.12em] uppercase mt-1">
            {item.category === 'portrait' ? 'Portrait' : 'Performance'}
          </p>
        </div>
      </div>

      {/* Expand icon */}
      <span className="absolute top-3 right-3 w-8 h-8 rounded-full bg-foreground-950/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
        <i className="ri-fullscreen-line text-foreground-50 text-sm"></i>
      </span>
    </div>
  );
}

export default function GalleryGrid() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeFilter, setActiveFilter] = useState<CategoryFilter>('All');
  const sectionRef = useScrollReveal<HTMLElement>(0.06, 'fade-up');

  const filteredItems = activeFilter === 'All'
    ? galleryItems
    : galleryItems.filter(item => {
        if (activeFilter === 'Portraits') return item.category === 'portrait';
        if (activeFilter === 'Performances') return item.category === 'performance';
        return true;
      });

  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % filteredItems.length);
  }, [filteredItems.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + filteredItems.length) % filteredItems.length);
  }, [filteredItems.length]);

  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowRight':
          goNext();
          break;
        case 'ArrowLeft':
          goPrev();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [lightboxOpen, closeLightbox, goNext, goPrev]);

  const currentItem = filteredItems[currentIndex];

  return (
    <>
      <section className="relative bg-background-50 px-6 md:px-10 lg:px-16 py-16 md:py-24">
        <div ref={sectionRef} className="max-w-6xl mx-auto">
          {/* Category filters */}
          <div className="flex flex-wrap items-center gap-3 mb-12">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`px-4 py-2 text-xs tracking-[0.12em] uppercase transition-all duration-300 cursor-pointer whitespace-nowrap rounded-full ${
                  activeFilter === cat
                    ? 'bg-accent-500 text-foreground-50 border border-accent-500'
                    : 'text-foreground-500 border border-foreground-200 hover:border-foreground-400 hover:text-foreground-700'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Masonry grid — staggered fade-in on scroll */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 auto-rows-[200px] md:auto-rows-[240px] gap-3 md:gap-4 grid-flow-dense">
            {filteredItems.map((item, index) => (
              <GalleryCard
                key={item.id}
                item={item}
                index={index}
                onClick={() => openLightbox(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/75 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            className="absolute top-6 right-6 z-10 w-10 h-10 flex items-center justify-center text-foreground-400 hover:text-foreground-50 transition-colors duration-300 cursor-pointer"
            onClick={closeLightbox}
            aria-label="Close lightbox"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          {/* Counter */}
          <div className="absolute top-6 left-6 z-10 text-foreground-400 text-xs tracking-[0.12em]">
            {currentIndex + 1} / {filteredItems.length}
          </div>

          {/* Prev */}
          <button
            className="absolute left-4 md:left-8 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full border border-foreground-50/20 flex items-center justify-center text-foreground-400 hover:text-foreground-50 hover:border-foreground-50/40 transition-all duration-300 cursor-pointer"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            aria-label="Previous image"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>

          {/* Image */}
          <div
            className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentItem.src}
              alt={currentItem.alt}
              className="max-w-full max-h-[75vh] object-contain"
            />
            {/* Caption */}
            <div className="mt-4 text-center">
              {currentItem.caption && (
                <p className="text-foreground-50 text-sm font-heading">{currentItem.caption}</p>
              )}
              <p className="text-foreground-500 text-xs tracking-[0.12em] uppercase mt-1">
                {currentItem.category === 'portrait' ? 'Portrait' : 'Performance'}
              </p>
            </div>
          </div>

          {/* Next */}
          <button
            className="absolute right-4 md:right-8 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full border border-foreground-50/20 flex items-center justify-center text-foreground-400 hover:text-foreground-50 hover:border-foreground-50/40 transition-all duration-300 cursor-pointer"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            aria-label="Next image"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>
      )}
    </>
  );
}